import { useLanguage } from "@/context/language"
import type { CoworkFile } from "@/cowork/api"
import { createMemo, ErrorBoundary, For, onCleanup, onMount, Show, Suspense, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { FileExtractedData } from "./file-extracted-data"
import "./file-inspection.css"

type InspectionMode = "document" | "data" | "split"

export function FileInspection(props: {
  file?: CoworkFile
  periodID?: string | null
  initialMode?: InspectionMode
  document: (file: CoworkFile) => JSX.Element
  empty?: JSX.Element
}) {
  const language = useLanguage()
  const [state, setState] = createStore({
    mode: props.initialMode ?? "document",
    wide: false,
    dataVisited: props.initialMode === "split" || props.initialMode === "data",
    split: 50, dragging: false,
  })
  let container: HTMLDivElement | undefined
  let panels: HTMLDivElement | undefined
  const resize = (value: number) => setState("split", Math.min(70, Math.max(30, Math.round(value))))
  const moveDivider = (event: PointerEvent) => {
    if (!state.dragging || !panels) return
    const bounds = panels.getBoundingClientRect()
    if (!bounds.width) return
    const value = ((event.clientX - bounds.left) / bounds.width) * 100
    resize(getComputedStyle(panels).direction === "rtl" ? 100 - value : value)
  }
  onMount(() => {
    if (!container) return
    const observer = new ResizeObserver(([entry]) => {
      const wide = entry.contentRect.width >= 900
      setState({ wide, dragging: wide && state.dragging })
    })
    observer.observe(container)
    onCleanup(() => observer.disconnect())
  })
  const mode = createMemo(() => (state.mode === "split" && !state.wide ? "document" : state.mode))
  const modes = createMemo<InspectionMode[]>(() => (state.wide ? ["document", "data", "split"] : ["document", "data"]))
  const label = (value: InspectionMode) =>
    language.t(
      value === "document"
        ? "cowork.accounting.extractions.section.document"
        : value === "data"
          ? "cowork.enterprise.data.extracted"
          : "cowork.files.extraction.split",
    )
  return (
    <div ref={(element) => (container = element)} class="file-inspection flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <Show when={props.file} fallback={props.empty}>
        <div
          class="file-inspection-toolbar flex shrink-0 gap-1 border-b border-v2-border-border-base p-2"
          role="group"
          aria-label={language.t("cowork.navigation.files")}
        >
          <For each={modes()}>
            {(value) => (
              <button
                type="button"
                class="rounded-md px-3 py-2 text-[12px] hover:bg-v2-overlay-simple-overlay-hover"
                classList={{ "bg-v2-background-bg-layer-03": mode() === value }}
                aria-pressed={mode() === value}
                onClick={() => setState({ mode: value, dragging: false, dataVisited: state.dataVisited || value !== "document" })}
              >
                {label(value)}
              </button>
            )}
          </For>
        </div>
        <div
          ref={panels}
          class="file-inspection-panels grid min-h-0 flex-1"
          data-mode={mode()} data-dragging={state.dragging ? "true" : undefined}
          style={{ "grid-template-columns": mode() === "split" ? `minmax(0, ${state.split}fr) 10px minmax(0, ${100 - state.split}fr)` : "minmax(0, 1fr)" }}
        >
          <div class="file-inspection-document min-h-0 min-w-0 overflow-auto" classList={{ hidden: mode() === "data" }}>
            <Show when={props.file} keyed>
              {(file) => (
                <InspectionBoundary>
                  <div class="h-full">{props.document(file)}</div>
                </InspectionBoundary>
              )}
            </Show>
          </div>
          <Show when={mode() === "split"}><div class="file-inspection-divider" role="separator" tabindex="0"
            aria-orientation="vertical" aria-valuemin={30} aria-valuemax={70} aria-valuenow={state.split}
            aria-label={language.t("cowork.files.extraction.split")}
            onDblClick={() => resize(50)}
            onPointerDown={(event) => { if (event.button !== 0) return; event.preventDefault(); event.currentTarget.focus(); event.currentTarget.setPointerCapture(event.pointerId); setState("dragging", true) }}
            onPointerMove={moveDivider}
            onPointerUp={(event) => { setState("dragging", false); if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId) }}
            onPointerCancel={() => setState("dragging", false)} onLostPointerCapture={() => setState("dragging", false)}
            onKeyDown={(event) => {
              const direction = panels && getComputedStyle(panels).direction === "rtl" ? -1 : 1
              if (event.key === "ArrowLeft") resize(state.split - 5 * direction)
              else if (event.key === "ArrowRight") resize(state.split + 5 * direction)
              else if (event.key === "Home") resize(30)
              else if (event.key === "End") resize(70)
              else if (event.key === "Enter") resize(50)
              else return
              event.preventDefault()
            }} /></Show>
          <div
            class="file-inspection-data min-h-0 min-w-0 overflow-auto"
            classList={{ hidden: mode() === "document" }}
          >
            <Show when={state.dataVisited}>
              <Show when={props.file} keyed>
                {(file) => (
                  <InspectionBoundary>
                    <FileExtractedData
                      file={file}
                      periodID={props.periodID ?? file.period_id ?? file.workflow_id}
                      active={mode() !== "document"}
                    />
                  </InspectionBoundary>
                )}
              </Show>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  )
}

function InspectionBoundary(props: { children: JSX.Element }) {
  const language = useLanguage()
  return (
    <ErrorBoundary
      fallback={(_error, reset) => (
        <div role="alert" class="p-4 text-[12px] text-v2-text-text-muted">
          {language.t("common.requestFailed")}
          <button type="button" class="ml-3 underline" onClick={reset}>
            {language.t("cowork.enterprise.workflows.reporting.retry")}
          </button>
        </div>
      )}
    >
      <Suspense
        fallback={
          <p role="status" class="p-4 text-[12px] text-v2-text-text-muted">
            {language.t("cowork.files.panel.loadingPreview")}
          </p>
        }
      >
        {props.children}
      </Suspense>
    </ErrorBoundary>
  )
}
